/**
 * Errors that are not ours to fix, shared by the Sentry filter and PostHog's
 * $exception capture so both report the same thing.
 *
 * Each rule names the issue it came from. Keep rules narrow: a pattern that
 * also matches a real application error hides a bug.
 */

export interface ExceptionLike {
  type?: string | null;
  value?: string | null;
  /** Stack frame file names / URLs, when the reporter has them. These are the
   *  RAW (minified bundle) URLs at filter time: Sentry and PostHog map them back
   *  to source files only after ingestion, so never match on package paths. */
  filenames?: Array<string | null | undefined>;
  /** True when the error reached a global unhandled-rejection handler. */
  unhandledRejection?: boolean;
}

/** Text that is never an application error, matched anywhere in "Type: value". */
export const NOISE_MESSAGES = [
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications',
  'Non-Error promise rejection captured',
  'Script error',
  // Microsoft's link scanner (Outlook/Defender) opens links in a stripped
  // browser and throws this from its own injected code.
  'Object Not Found Matching Id',
  'Network request failed',
  // posthog-js gives up on a slow flag/ingest request (LIGHTMASTER-WEB-23).
  'PostHog request timed out',
] as const;

/**
 * A third-party beacon that a blocker, a flaky network or a closing tab
 * stopped. Sentry appends the host: "Failed to fetch (analytics.google.com)".
 * LIGHTMASTER-WEB-1T (mapbox telemetry, 54 events), -1V/-1X/-1Z/-3C (GA4).
 */
const THIRD_PARTY_FETCH =
  /Failed to fetch \((?:events\.mapbox\.com|[\w.-]*analytics\.google\.com|[\w.-]*google-analytics\.com|[\w.-]*googletagmanager\.com|[\w.-]*posthog\.com)\)/;

/** Code a browser extension injected (PayPal Honey: LIGHTMASTER-WEB-1R/-1Q). */
const EXTENSION_FRAME =
  /^(?:chrome|moz|safari(?:-web)?|ms-browser)-extension:|\.safariextension\/|\.appex\/Contents\/Resources\//;

/**
 * A cancelled request that nobody awaited: an AbortError reaching the global
 * unhandled-rejection handler. mapbox-gl does this for every in-flight tile
 * when a map is removed (leaving a proposal or quote page) — LIGHTMASTER-WEB-1S
 * /-1B, 91 events. A cancellation cannot be a user-visible failure; AbortErrors
 * our own code reports with captureException are handled, and still reach us.
 * (The first version of this rule required "mapbox-gl" in the stack frames and
 * never matched in production: frames are minified bundle URLs at filter time.)
 */
const CANCELLATION = /^(?:AbortError|DOMException|Error): (?:AbortError: )?(?:signal is aborted without reason|The user aborted a request\.?|The operation was aborted\.?|This operation was aborted\.?|Fetch is aborted|Request aborted)$/;
function isUnawaitedCancellation(e: ExceptionLike, text: string): boolean {
  return e.unhandledRejection === true && CANCELLATION.test(text.trim());
}

/** mapbox-gl calls its own error callback after the map cleared it (LIGHTMASTER-WEB-37). No code of ours has an `errorCb`. */
const MAPBOX_CLEARED_CALLBACK = 'this.errorCb is not a function';

export function isNoiseException(e: ExceptionLike): boolean {
  const text = `${e.type ?? ''}: ${e.value ?? ''}`;
  if (NOISE_MESSAGES.some((m) => text.includes(m)) || text.includes(MAPBOX_CLEARED_CALLBACK)) return true;
  if (THIRD_PARTY_FETCH.test(text)) return true;
  if ((e.filenames ?? []).some((f) => typeof f === 'string' && EXTENSION_FRAME.test(f))) return true;
  return isUnawaitedCancellation(e, text);
}

/**
 * PostHog $exception events carry the same data as `$exception_list` entries.
 * The whole event is noise only when every listed exception is.
 */
export function isNoisePostHogException(properties: Record<string, unknown> | undefined): boolean {
  const list = properties?.$exception_list;
  if (!Array.isArray(list) || list.length === 0) return false;
  return list.every((entry) => {
    const ex = (entry ?? {}) as { type?: string; value?: string; mechanism?: { type?: string; handled?: boolean }; stacktrace?: { frames?: Array<{ filename?: string; abs_path?: string }> } };
    const frames = ex.stacktrace?.frames ?? [];
    return isNoiseException({
      type: ex.type,
      value: ex.value,
      filenames: frames.flatMap((f) => [f.filename, f.abs_path]),
      // posthog-js marks autocaptured errors and rejections handled:false with a
      // 'generic' mechanism type (capture_exceptions here is unhandled-only).
      unhandledRejection: ex.mechanism?.handled === false,
    });
  });
}
